import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Book, Download, File, Share2, Calendar, Clock, Tag, Facebook, Twitter, Linkedin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LoadingIndicator from '../components/common/LoadingIndicator';
import { fetchBareActBySlug, BareActWithSections} from '../services/bareActService';

const BareActDetailPage: React.FC = () => {
  const { actSlug } = useParams<{ actSlug: string }>();
  const [act, setAct] = useState<BareActWithSections | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<'overview' | 'summary' | 'sections'>('overview');
  const { t, isHindi } = useLanguage();

  useEffect(() => {
    const fetchAct = async () => {
      try {
        setLoading(true);
        
        if (!actSlug) {
          setError('Invalid act URL');
          setLoading(false);
          return;
        }
        
        const actData = await fetchBareActBySlug(actSlug);
        
        if (!actData) {
          setError('Act not found');
          setLoading(false);
          return;
        }
        
        setAct(actData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching bare act:', err);
        setError('Failed to load the requested act. Please try again later.');
        setLoading(false);
      }
    };

    fetchAct();
  }, [actSlug]);

  useEffect(() => {
    if (act) {
      document.title = `${act.title} | Bare Acts | Legal Nest`;
    }
  }, [act]);

  // Helper function to handle sharing
  const handleShare = (platform: string) => {
    if (!act) return;
    
    const url = window.location.href;
    const title = act.title;
    let shareUrl = '';
    
    switch(platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(url)
          .then(() => alert('Link copied to clipboard!'))
          .catch(err => console.error('Error copying to clipboard:', err));
        return;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="container-custom py-8">
        <Link to="/bare-acts" className="inline-flex items-center text-neutral-500 hover:text-primary-500 mb-6">
          <ArrowLeft size={16} className="mr-1" />
          Back to Bare Acts
        </Link>
        <LoadingIndicator message="Loading act details..." />
      </div>
    );
  }

  if (error || !act) {
    return (
      <div className="container-custom py-8">
        <Link to="/bare-acts" className="inline-flex items-center text-neutral-500 hover:text-primary-500 mb-6">
          <ArrowLeft size={16} className="mr-1" />
          Back to Bare Acts
        </Link>
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <Book size={48} className="mx-auto text-neutral-300 mb-4" />
          <h2 className="text-2xl font-serif font-bold mb-2">Act Not Found</h2>
          <p className="text-neutral-600 mb-6">
            {error || "We couldn't find the bare act you're looking for."}
          </p>
          <Link to="/bare-acts" className="btn btn-primary">
            Browse All Acts
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-custom py-8">
      {/* Back link */}
      <Link to="/bare-acts" className="inline-flex items-center text-neutral-500 hover:text-primary-500 mb-6">
        <ArrowLeft size={16} className="mr-1" /> {t('Back To Bare Acts')}
      </Link>

      <article>
        {/* Act header */}
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-4">
            {isHindi && act.title_hi ? act.title_hi : act.title}
          </h1>
          <div className="flex flex-wrap items-center text-sm text-neutral-600 mb-6 gap-4">
            <div className="flex items-center">
              <Calendar size={16} className="mr-1" />
              <span>Year: {act.year}</span>
            </div>
            <div className="flex items-center">
              <Clock size={16} className="mr-1" />
              <span>Last updated: {new Date(act.last_updated).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center">
              <Tag size={16} className="mr-1" />
              <span>{act.category}</span>
            </div>
          </div>

          {act.image_url && (
            <div className="mb-6">
              <img
                src={act.image_url}
                alt={act.title}
                className="w-full h-auto rounded-lg"
              />
            </div>
          )}
        </header>

        {/* Tabs navigation */}
        <div className="border-b border-neutral-200 mb-6">
          <div className="flex flex-wrap -mb-px">
            <button 
              className={`mr-8 py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'overview' 
                ? 'border-primary-500 text-primary-600' 
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'}`}
              onClick={() => setCurrentTab('overview')}
            >
              {t('Overview')}
            </button>
            <button 
              className={`mr-8 py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'summary' 
                ? 'border-primary-500 text-primary-600' 
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'}`}
              onClick={() => setCurrentTab('summary')}
            >
              {t('Summary')}
            </button>
            <button 
              className={`mr-8 py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'sections' 
                ? 'border-primary-500 text-primary-600' 
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'}`}
              onClick={() => setCurrentTab('sections')}
            >
              {t('Sections')}
            </button>
          </div>
        </div>

        {/* Main content area with proper prose styling */}
        <div className="prose prose-lg max-w-none">
          {/* Overview tab */}
          {currentTab === 'overview' && (
            <div>
              <p>{isHindi && act.description_hi ? act.description_hi : act.description}</p>
              
              <h3 className="text-xl font-serif font-bold mt-6 mb-4">{t('Purpose and Scope')}</h3>
              <p>
                {isHindi ? (
                  <>
                    {act.title_hi || act.title} को वर्ष {act.year} में भारत में {act.category.toLowerCase()} के लिए एक व्यापक कानूनी ढांचा प्रदान करने के लिए अधिनियमित किया गया था। यह प्रमुख सिद्धांतों, प्रक्रियाओं और {act.slug.split('-').join(' ')} से संबंधित मामलों को विनियमित करने के लिए प्राधिकरणों की स्थापना करता है।
                  </>
                ) : (
                  <>
                    The {act.title} was enacted in {act.year} to provide a comprehensive legal framework 
                    for {act.category.toLowerCase()} in India. It establishes key principles, procedures, and 
                    authorities to regulate matters related to {act.slug.split('-').join(' ')}.
                  </>
                )}
              </p>
              
              <h3 className="text-xl font-serif font-bold mt-6 mb-4">{t('Key Features')}</h3>
              <ul>
                <li>{isHindi ? `${act.category.toLowerCase()} मामलों का व्यापक कवरेज` : `Comprehensive coverage of ${act.category.toLowerCase()} matters`}</li>
                <li>{isHindi ? 'स्पष्ट परिभाषाएँ और प्रक्रियात्मक दिशानिर्देश' : 'Clear definitions and procedural guidelines'}</li>
                <li>{isHindi ? 'प्रासंगिक नियामक प्राधिकरणों की स्थापना' : 'Establishment of relevant regulatory authorities'}</li>
                <li>{isHindi ? 'दंड और उपचार के प्रावधान' : 'Provisions for penalties and remedies'}</li>
                <li>{isHindi ? 'उभरती चुनौतियों को संबोधित करने के लिए नियमित अपडेट' : 'Regular updates to address emerging challenges'}</li>
              </ul>
            </div>
          )}
          
          {/* Full text tab */}
          {currentTab === 'summary' && (
            <div>
              {act.content ? (
                <div>
                  {(isHindi && act.content_hi ? act.content_hi : act.content).split('\n\n').map((paragraph, index) => (
                    <p key={index} className="mb-4">{paragraph}</p>
                  ))}
                </div>
              ) : (
                <div className="bg-neutral-50 p-6 rounded-lg text-center">
                  <File size={48} className="text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-xl font-serif font-bold mb-2">{t('full_text_not_available')}</h3>
                  <p className="text-neutral-600 mb-4">
                    {t('full_text_not_available_message')}
                  </p>
                  {act.pdf_url && (
                    <a 
                      href={act.pdf_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn btn-primary"
                    >
                      <Download size={16} className="mr-2" />
                      {t('download_pdf_instead')}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Sections tab */}
          {currentTab === 'sections' && (
            <div>
              {act.sections && act.sections.length > 0 ? (
                <div className="space-y-6">
                  {act.sections.map((section, index) => (
                    <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                      <h3 className="text-lg font-serif font-bold mb-2">
                        {t('section')} {section.section_number}: {isHindi && section.title_hi ? section.title_hi : section.title}
                      </h3>
                      <p className="text-neutral-700">
                        {isHindi && section.content_hi ? section.content_hi : section.content}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-neutral-50 p-6 rounded-lg text-center">
                  <File size={48} className="text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-xl font-serif font-bold mb-2">{t('Sections Not Available')}</h3>
                  <p className="text-neutral-600 mb-4">
                    {t('Sections Not Available Message')}
                  </p>
                  {act.pdf_url && (
                    <a 
                      href={act.pdf_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn btn-primary"
                    >
                      <Download size={16} className="mr-2" />
                      {t('Download Pdf Instead')}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Citation format */}
        <div className="bg-neutral-100 p-6 rounded-lg my-8">
          <h3 className="font-serif font-bold mb-4">{t('how_to_cite')}</h3>
          <p className="text-neutral-700 mb-2">{t('for_academic_citations')}:</p>
          <div className="bg-white p-4 rounded border border-neutral-200 font-mono text-sm break-all">
            {act.title}, {act.year}, Act No. XX of {act.year}, India.
          </div>
        </div>

        {/* PDF download section */}
        {act.pdf_url && (
          <div className="my-8 p-4 border border-primary-100 rounded-lg bg-primary-50">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start sm:items-center">
                <File size={24} className="text-primary-600 mr-3 shrink-0 mt-1 sm:mt-0" />
                <div>
                  <p className="font-medium text-primary-800">
                    Download Complete Version
                  </p>
                  <p className="text-sm text-primary-600">
                    Official document as published by the Government
                  </p>
                </div>
              </div>
              <a 
                href={act.pdf_url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn btn-primary flex items-center w-full sm:w-auto justify-center"
              >
                <Download size={16} className="mr-2" />
                Download PDF
              </a>
            </div>
          </div>
        )}

        {/* Share buttons */}
        <div className="mt-8 pt-6 border-t border-neutral-200">
          <h3 className="font-serif font-bold mb-4">Share this act</h3>
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => handleShare('facebook')}
              className="inline-flex items-center px-3 py-1 bg-neutral-100 hover:bg-neutral-200 rounded-full text-sm transition-colors"
            >
              <Facebook size={14} className="mr-1" />
              Share
            </button>
            <button 
              onClick={() => handleShare('twitter')}
              className="inline-flex items-center px-3 py-1 bg-neutral-100 hover:bg-neutral-200 rounded-full text-sm transition-colors"
            >
              <Twitter size={14} className="mr-1" />
              Tweet
            </button>
            <button 
              onClick={() => handleShare('linkedin')}
              className="inline-flex items-center px-3 py-1 bg-neutral-100 hover:bg-neutral-200 rounded-full text-sm transition-colors"
            >
              <Linkedin size={14} className="mr-1" />
              LinkedIn
            </button>
            <button 
              onClick={() => handleShare('copy')}
              className="inline-flex items-center px-3 py-1 bg-neutral-100 hover:bg-neutral-200 rounded-full text-sm transition-colors"
            >
              <Share2 size={14} className="mr-1" />
              Copy Link
            </button>
          </div>
        </div>

        {/* Author/source section */}
        <div className="mt-8 pt-6 border-t border-neutral-200">
          <div className="bg-neutral-50 p-6 rounded-lg">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-primary-100 text-primary-500 rounded-full flex items-center justify-center mr-4">
                <Book size={20} className="text-primary-600" />
              </div>
              <div>
                <h3 className="font-serif font-bold">Legal Source</h3>
                <p className="text-sm text-neutral-600">Government Publication</p>
              </div>
            </div>
            <p className="text-neutral-700">
              This bare act is sourced from official government publications and legal databases. Legal Nest maintains a comprehensive repository of Indian laws and regulations for reference and educational purposes.
            </p>
          </div>
        </div>
      </article>
      
      {/* Related Acts */}
      {act.related_acts && act.related_acts.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-serif font-bold mb-6">Related Acts</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {act.related_acts.slice(0, 4).map((relatedAct) => (
              <div key={relatedAct.id} className="flex flex-col h-full">
                <Link to={`/bare-acts/${relatedAct.slug}`} className="block mb-2">
                  <div className="h-40 bg-neutral-100 rounded-lg flex items-center justify-center">
                    <Book size={32} className="text-neutral-400" />
                  </div>
                </Link>
                <h3 className="text-base font-medium line-clamp-2 mb-1">
                  <Link 
                    to={`/bare-acts/${relatedAct.slug}`}
                    className="hover:text-primary-500"
                  >
                    {relatedAct.title}
                  </Link>
                </h3>
                <div className="text-xs text-neutral-500 mt-1">
                  {relatedAct.year}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BareActDetailPage;